import LogOutIcon from '@src/svgs/LogOutIcon';
import { contrastingTextColor } from '@src/utility/colorContrast';
import { initialsFromName } from '@src/utility/initials';
import { parsePrimaryBrandColor } from '@src/utility/showcaseBranding';
import { memo, type ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';

import type { Showcase } from '@src/types.ts/story';

type OrganizationAvatarTooltipProps = {
  email?: string;
  onLogOut: () => void;
  topChildren?: ReactNode;
  bottomChildren?: ReactNode;
  /** Compact mode: render avatar+name+email header, restyle action rows. */
  compact?: boolean;
  album?: Showcase | null;
  username?: string | null;
};

function OrganizationAvatarTooltip({
  onLogOut,
  email,
  topChildren,
  bottomChildren,
  compact = false,
  album,
  username,
}: OrganizationAvatarTooltipProps) {
  const [logOutHover, setLogOutHover] = useState(false);

  if (compact) {
    const brandColor = parsePrimaryBrandColor(album?.branding?.primaryColors);
    const logoUrl = album?.logo?.url || album?.mainImage?.url;
    const initials = initialsFromName(album?.title || username);
    const initialsBg = brandColor ?? 'var(--accent)';
    const initialsFg = brandColor
      ? contrastingTextColor(brandColor)
      : 'var(--gray-900)';

    return (
      <Wrapper $compact>
        <Header>
          <HeaderAvatar>
            {logoUrl ? (
              <img src={`${logoUrl}?w=64&h=64&fit=crop`} alt="" />
            ) : (
              <HeaderInitials $bg={initialsBg} $fg={initialsFg}>
                {initials}
              </HeaderInitials>
            )}
          </HeaderAvatar>
          <HeaderText>
            <HeaderName title={username ?? undefined}>
              {username || album?.title || 'Account'}
            </HeaderName>
            {email && <HeaderEmail title={email}>{email}</HeaderEmail>}
          </HeaderText>
        </Header>

        {topChildren && (
          <>
            <DividerLine />
            {topChildren}
          </>
        )}

        <DividerLine />

        <CompactActionRow
          onClick={() => onLogOut()}
          onMouseEnter={() => setLogOutHover(true)}
          onMouseLeave={() => setLogOutHover(false)}
        >
          <CompactIcon>
            <LogOutIcon
              fillColor={logOutHover ? 'var(--primary)' : undefined}
            />
          </CompactIcon>
          <CompactLabel $color={logOutHover ? 'var(--primary)' : undefined}>
            Log out
          </CompactLabel>
        </CompactActionRow>

        {bottomChildren}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {topChildren}
      <Section
        clickable
        onClick={() => onLogOut()}
        onMouseEnter={() => setLogOutHover(true)}
        onMouseLeave={() => setLogOutHover(false)}
      >
        <LogOutIcon fillColor={logOutHover ? 'var(--primary)' : undefined} />
        <SectionText color={logOutHover ? 'var(--primary)' : undefined}>
          Log Out
        </SectionText>
      </Section>
      {email && (
        <Section>
          <EmailSectionText title={email}>{email}</EmailSectionText>
        </Section>
      )}
      {bottomChildren}
    </Wrapper>
  );
}

export default memo(OrganizationAvatarTooltip);

export const Wrapper = styled.div<{ $compact?: boolean }>`
  display: inline-flex;
  padding: ${p => (p.$compact ? '12px' : '16px')};
  flex-direction: column;
  align-items: stretch;
  gap: ${p => (p.$compact ? '8px' : '16px')};

  border-radius: ${p => (p.$compact ? '10px' : '8px')};
  border: 1px solid
    ${p => (p.$compact ? 'var(--border-subtle)' : 'var(--border)')};
  background: ${p =>
    p.$compact ? 'var(--surface-subtle)' : 'var(--background)'};
  box-shadow: var(--elevation-toast);

  width: ${p => (p.$compact ? '240px' : '197px')};
`;

export const Section = styled.div<{ clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  ${props =>
    props.clickable &&
    css`
      cursor: pointer;
    `}
`;

export const SectionText = styled.div<{ color?: string }>`
  color: ${props => props.color || 'var(--text-secondary)'};
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-style: normal;
  font-weight: var(--font-weight-medium);
  line-height: normal;
`;

export const EmailSectionText = styled(SectionText)`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  margin-left: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 24px);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px 8px;
  min-width: 0;
`;

const HeaderAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const HeaderInitials = styled.div<{ $bg: string; $fg: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$bg};
  color: ${p => p.$fg};
  font-size: 13px;
  font-weight: var(--font-weight-semi-bold);
  letter-spacing: 0.2px;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const HeaderName = styled.span`
  color: var(--foreground);
  font-size: 13px;
  font-weight: var(--font-weight-semi-bold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderEmail = styled.span`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: var(--font-weight-regular);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DividerLine = styled.div`
  height: 1px;
  background: var(--border-subtle);
  margin: 2px 0;
`;

const CompactActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 10px 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-secondary);
  transition:
    background var(--transition-fast) ease,
    color var(--transition-fast) ease;

  &:hover {
    background: var(--sidebar-hover);
  }
`;

const CompactIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const CompactLabel = styled.span<{ $color?: string }>`
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  color: ${p => p.$color || 'inherit'};
`;
